import parsePhoneNumber from 'libphonenumber-js';

import * as Yup from 'yup';
import { EmailValidator, PhoneValidator } from 'types/Validators';
import { setLocale } from 'yup';
import { isEmpty } from 'lodash';
import captureException from './captureException';

setLocale({
  // use constant translation keys for messages without values
  string: {
    email: 'validations.email',
  },
});

export function buildPhoneSchema(phoneValidator: PhoneValidator): Yup.StringSchema {
  const syntaticalSchema = Yup.string()
    .required('validations.phone')
    .test({
      name: 'phone',
      message: 'validations.phone',
      test: (value) => {
        if (isEmpty(value)) {
          return true;
        }

        return parsePhoneNumber(value, 'US')?.isValid() ?? false;
      },
    });

  return Yup.string().test('is-legit-phone', 'validations.phone', async (value) => {
    if (value && (await syntaticalSchema.isValid(value))) {
      try {
        return await phoneValidator(value);
      } catch (e) {
        captureException(e);
        return true;
      }
    }

    return false;
  });
}

export function buildEmailSchema(emailValidator: EmailValidator): Yup.StringSchema {
  const syntaticalSchema = Yup.string().required('validations.email').email();

  return Yup.string().test('is-legit-email', 'validations.email', async (value) => {
    if (value && (await syntaticalSchema.isValid(value))) {
      try {
        return (await emailValidator(value)).valid;
      } catch (e) {
        captureException(e);
        return true;
      }
    }

    return false;
  });
}

interface BuildLeadFormSchemaOptions {
  emailValidator: EmailValidator;
  phoneValidator: PhoneValidator;
  showZipCode?: boolean;
  showAddress?: boolean;
}

export function buildLeadFormSchema({
  emailValidator,
  phoneValidator,
}: BuildLeadFormSchemaOptions): Yup.ObjectSchema {
  return Yup.object().shape({
    name: Yup.string()
      .matches(/^\S{2,} .*\S{2,}\s*$/, 'validations.name')
      .required('validations.name'),
    email: buildEmailSchema(emailValidator),
    phone: buildPhoneSchema(phoneValidator),
    zip: Yup.string()
      .matches(/^\d{5}(-\d{4})?$/, 'validations.zip')
      .when('$zipRequired', (zipRequired: boolean, schema: Yup.StringSchema) =>
        zipRequired ? schema.required('validations.zip') : schema.notRequired()
      ),
    address: Yup.string().when(
      '$addressRequired',
      (addressRequired: boolean, schema: Yup.StringSchema) =>
        addressRequired ? schema.required('validations.address') : schema.notRequired()
    ),
  });
}

export function buildLoginFormSchema({
  emailValidator,
}: {
  emailValidator: EmailValidator;
}): Yup.ObjectSchema {
  return Yup.object().shape({
    email: buildEmailSchema(emailValidator),
    password: Yup.string().required(),
  });
}

export const ForgotPasswordFormSchema = Yup.object().shape({
  email: Yup.string().email().required(),
});
